
import { toAnalyticsId } from '@shared/lib/amplitude'

import { trackClassroomEvent } from '../../lib/classroom'

interface LessonRoomAnalyticsReturn {
  trackConnected: (isReconnect: boolean) => void
  trackDisconnected: (reason: string | null) => void
  trackClickMic: () => void
  trackClickCam: () => void
  trackClickShare: () => void
  trackPageView: () => void
  trackClickSettings: () => void
  trackSelectMicro: (deviceId: string) => void
  trackSelectCamera: (deviceId: string) => void
  trackSelectSpeaker: (deviceId: string) => void
  trackClickGuide: () => void
}

const SELF = { name: 'self', is_self: true, user_role: 'customer' } as const

/** The room never came up, as opposed to a session that dropped. */
const FAILED_REASONS = ['CONNECT_FAILED', 'TOKEN_MISSING', 'UNSUPPORTED_BROWSER']

const trackButton = (name: string, text: string): void => {
  trackClassroomEvent('Button_click', { name, text })
}

// The device id is a stable per-origin hash, so only whether the default device was kept
const trackDevice = (name: string, deviceId: string): void => {
  trackClassroomEvent('Field_change', { name, value: deviceId === 'default' ? 'default' : 'other' })
}

export const useLessonRoomAnalytics = (): LessonRoomAnalyticsReturn => ({
  trackConnected: (isReconnect) => {
    if (isReconnect) {
      trackClassroomEvent('Connection_change', { ...SELF, connection_status: 'connected' })
    } else {
      trackClassroomEvent('Room_join', SELF)
    }
  },
  trackDisconnected: (reason) => {
    if (reason === 'CLIENT_INITIATED') {
      trackClassroomEvent('Room_leave', SELF)
      return
    }

    trackClassroomEvent('Connection_change', {
      ...SELF,
      connection_status: reason && FAILED_REASONS.includes(reason) ? 'failed' : 'disconnected',
      reason: reason ? toAnalyticsId(reason) : null,
    })
  },
  trackClickMic: () => trackButton('mic', 'Mic'),
  trackClickCam: () => trackButton('camera', 'Camera'),
  trackClickShare: () => trackButton('share', 'Share'),
  trackPageView: () => trackClassroomEvent('Page_view', { name: 'lesson_room' }),
  trackClickSettings: () => trackButton('settings', 'Settings'),
  trackSelectMicro: (deviceId) => trackDevice('microphone_device', deviceId),
  trackSelectCamera: (deviceId) => trackDevice('camera_device', deviceId),
  trackSelectSpeaker: (deviceId) => trackDevice('speaker_device', deviceId),
  trackClickGuide: () => trackButton('how_to_join', 'How to join'),
})
